import React, { useEffect, useState } from "react";
import {
	ComposableMap,
	Geographies,
	Geography,
	Marker,
} from "react-simple-maps";
import LondonJson from "../data/London.topo.json";
import { scaleLinear } from "d3-scale";
import {
	Typography,
	TableContainer,
	Table,
	TableHead,
	TableBody,
	TableCell,
	TableRow,
} from "@mui/material";
import "../css/style.css";
import axios from "axios";
import { InfoRounded } from "@mui/icons-material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";

function Home() {
	const [selectedBorough, setSelectedBorough] = useState(null);
	const [heatmapData, setHeatmapData] = useState();
	const [rsdatg, setRsdtag] = useState();
	const [rsi, setRsi] = useState();
	const [dataReady, setDataReady] = useState(false);
	const [searchArea, setSearchArea] = useState("Centers");
	const [searchFieldValue, setSearchFieldValue] = useState(null);
	const [colorPalette, setColorPalette] = useState([]);
	const [rsdatgAppear, setRsdtagAppear] = useState([]);
	const [rsiAppear, setRsiAppear] = useState([]);
	const [heatmapSource, setHeatmapSource] = useState("chain_roughsleeping");

	const heatmapIntervals = {
		chain_roughsleeping: [100, 200, 300, 500],
		// roughsleeping_count: [10, 20, 30, 40],
	};

	const boroughs = LondonJson.objects.lad.geometries.map(
		(LondonJsonBorough) => {
			return LondonJsonBorough.properties.LAD13NM;
		}
	);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		axios({
			action: "GET",
			url: "/data-dashboard/data",
		}).then((response) => {
			setHeatmapData(JSON.parse(response.data["heatmap_data"]));
			setRsdtag(JSON.parse(response.data["rsdatg"]));
			setRsi(JSON.parse(response.data["rsi"]));
			setDataReady(true);
		});
	}, []);

	useEffect(() => {
		if (heatmapData) {
			let minValueBoroughs = Number.POSITIVE_INFINITY;
			let maxValueBoroughs = Number.NEGATIVE_INFINITY;

			heatmapData.forEach((boroughData) => {
				if (boroughs.includes(boroughData["London Borough"])) {
					if (boroughData[heatmapSource] < minValueBoroughs)
						minValueBoroughs = boroughData[heatmapSource];
					if (boroughData[heatmapSource] > maxValueBoroughs)
						maxValueBoroughs = boroughData[heatmapSource];
				}
			});
			const colorPaletteTmp = Array.from(
				{ length: heatmapIntervals[heatmapSource].length + 1 },
				(_, index) =>
					scaleLinear()
						.domain([0, heatmapIntervals[heatmapSource].length])
						.range(["#e4b3f2", "#651e7a"])(index)
			);
			setColorPalette(colorPaletteTmp);
			for (let i = 0; i < heatmapData.length; i++) {
				if (boroughs.includes(heatmapData[i]["London Borough"])) {
					let intervalIndex = heatmapIntervals[
						heatmapSource
					].findIndex(
						(interval) => heatmapData[i][heatmapSource] < interval
					);

					if (intervalIndex === -1) {
						intervalIndex = heatmapIntervals[heatmapSource].length;
					}
					heatmapData[i][heatmapSource + "_colour"] =
						colorPaletteTmp[intervalIndex];
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [heatmapData, heatmapSource]);

	useEffect(() => {
		if (selectedBorough) {
			if (dataReady) {
				setRsiAppear(
					rsi.filter(
						(row) => row["London Borough"] === selectedBorough
					)[0]
				);
				setRsdtagAppear(
					rsdatg.filter(
						(row) => row["London Borough"] === selectedBorough
					)[0]
				);
			}
			setSearchArea("Boroughs");
			setSearchFieldValue(selectedBorough);
		} // eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedBorough]);

	useEffect(() => {
		if (searchFieldValue) {
			if (searchArea === "Centers") {
				setTimeout(() => {
					document
						.getElementById("data-dashboard-bottom-container")
						.scrollIntoView({
							behavior: "smooth",
						});
				}, 150);
			} else if (searchArea === "Services") {
				if (searchFieldValue !== "") {
				}
			} else if (searchArea === "Boroughs") {
				setSelectedBorough(searchFieldValue);
			}
		} // eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchFieldValue]);

	const tooltipHover = (londonBorough) => {
		return (
			<>
				<b>{londonBorough}</b>
				<br />
				CHAIN Rough sleeping
				<br />
				{
					heatmapData.filter(
						(row) => row["London Borough"] === londonBorough
					)[0][heatmapSource]
				}
			</>
		);
	};

	const CustomTooltip = styled(({ className, ...props }) => (
		<Tooltip {...props} classes={{ popper: className }} />
	))(({ theme }) => ({
		[`& .${tooltipClasses.tooltip}`]: {
			backgroundColor: "white",
			color: "#333",
			boxShadow: "0 3px 14px rgba(0,0,0,0.4)",
			padding: "14px 25px 14px 21px",
			fontSize: "13px",
			borderRadius: "12px",
			transform: "translate3d(531px, 209px, 0px);",
			transition: "opacity 0.2s linear",
			marginBottom: 20,
			transformOrigin: "0 0",
		},
	}));

	return (
		<div id="data-dashboard-main">
			<div className="introduction-text-container">
				<div className="introduction-text-title">Data Dashboard</div>
				<div className="introduction-text-description">
					<Typography
						variant="body1"
						sx={{ fontSize: 18, fontWeight: "bold" }}
					>
						This dashboard summarises data on the number of people
						rough sleeping in London and funding allocations from
						central government homelessness and rough sleeping
						programmes.
					</Typography>
				</div>
			</div>
			{dataReady ? (
				<div id="data-dashboard-top-container">
					<div id="data-dashboard-map-container">
						<div id="data-dashboard-map-source-selection">
							<Typography
								className={
									heatmapSource === "chain_roughsleeping"
										? "heatmap-source-button-selected"
										: "heatmap-source-button"
								}
								style={{ border: "none" }}
								variant="body1"
								onClick={() =>
									setHeatmapSource("chain_roughsleeping")
								}
							>
								Count of people rough sleeping (CHAIN 2022/23)
								<CustomTooltip
									title={
										<div>
											Data according to The Combined
											Homelessness and Information Network
											(CHAIN) and retrieved from the Atlas
											of London's Homelessness Services.
											These figures present information
											about the total number of
											individuals who are seen by outreach
											teams sleeping rough in each London
											Local Authority in 2022/23. CHAIN is
											a multi-agency monitoring system
											used by outreach teams across London
											to record the contacts they have
											with people sleeping rough. CHAIN
											data is regarded as some of the most
											reliable information on rough
											sleeping available nationally. It
											has the benefit of being used and
											updated consistently over time, as
											opposed to relying on a snapshot
											taken on a single night. A
											limitation of the system is that
											some people stay in very hidden or
											difficult to access sites and are
											unwilling to disclose where they
											stay. The system is commissioned by
											the GLA and managed by Homeless
											Link. Regular reports are published
											by the GLA. CHAIN records more than
											just rough sleeping information and
											more details can be found on the{" "}
											<a
												className="homeless_link"
												href="https://homeless.org.uk/what-we-do/streetlink-and-chain/chain/"
												target="_blank"
												rel="noreferrer"
											>
												Homeless Link website
											</a>
											.
										</div>
									}
									placement="bottom-end"
									enterTouchDelay={0}
								>
									<InfoRounded
										style={{
											color:
												heatmapSource ===
												"chain_roughsleeping"
													? "#A569B6"
													: "#999999",
											height: "15px",
											width: "15px",
											marginLeft: 7,
											cursor: "pointer",
										}}
									/>
								</CustomTooltip>
							</Typography>
							{/* <Typography
								style={{
									marginLeft: 30,
								}}
								variant="body1"
								className={
									heatmapSource === "roughsleeping_count"
										? "heatmap-source-button-selected"
										: "heatmap-source-button"
								}
								onClick={() =>
									setHeatmapSource("roughsleeping_count")
								}
							>
								DLUHC street count/estimates
								<CustomTooltip
									title={
										<div>
											Data according to DLUHC rough
											sleeper counts/estimates for 2021
											and retrieved from the Atlas of
											London's Homelessness Services.
											Rough sleeping counts and estimates
											are single night snapshots of the
											number of people found sleeping
											rough in Local Authority areas. The
											figures represent the number of
											people seen or thought to be
											sleeping rough in the Local
											Authority area on a “typical night”
											– a single date chosen by the local
											authority between 1 October and 30
											November. In London, most street
											counts are done on a single night in
											November to avoid double-counting.
											London Local Authorities tend do a
											physical count rather than producing
											an estimate. In a physical count,
											teams of volunteers go through the
											Local Authority search areas that
											are known or likely to be occupied
											by rough sleepers. A limitation of
											such methods is the difficulty to
											identify all rough sleepers because
											some people are very hidden and
											weather conditions can affect the
											number of people in visible
											locations. More information about
											rough sleeper counts and estimates
											can be found on the{" "}
											<a
												className="homeless_link"
												href="https://www.gov.uk/government/statistics/rough-sleeping-snapshot-in-england-autumn-2022/rough-sleeping-snapshot-in-england-autumn-2022-technical-report#data-quality-and-limitations"
												target="_blank"
												rel="noreferrer"
											>
												DLUHC website
											</a>
											.
										</div>
									}
									placement="bottom-end"
									enterTouchDelay={0}
								>
									<InfoRounded
										style={{
											color:
												heatmapSource ===
												"roughsleeping_count"
													? "#A569B6"
													: "#999999",
											height: "15px",
										}}
									/>
								</CustomTooltip>
							</Typography> */}
						</div>
						<div id="london-map">
							<ComposableMap
								projection="geoMercator"
								projectionConfig={{
									scale: 80000,
									rotate: [3, -55, -10],
									center: [1.2, -3.74],
								}}
							>
								<Geographies geography={LondonJson}>
									{({ geographies }) =>
										geographies.map((geo) => {
											const isBoroughSelected =
												selectedBorough ===
												geo.properties.LAD13NM;

											const currentBorough =
												heatmapData.filter(
													(boroughData) =>
														boroughData[
															"London Borough"
														] ===
														geo.properties.LAD13NM
												)[0];

											return (
												<>
													<CustomTooltip
														title={tooltipHover(
															geo.properties
																.LAD13NM
														)}
														followCursor={true}
														arrow
														placement="top"
														key={
															"geo-" +
															geo.properties
																.LAD13CDO
														}
														sx={{
															"& .MuiTooltip-arrow":
																{
																	color: "white",
																},
														}}
													>
														<Geography
															key={geo.rsmKey}
															geography={geo}
															stroke={
																isBoroughSelected
																	? "#fe9e8c"
																	: "#F5F5F5"
															}
															strokeWidth={
																isBoroughSelected
																	? 3
																	: 0.8
															}
															strokeOpacity={
																isBoroughSelected
																	? 1
																	: 0.8
															}
															fill={
																heatmapData.find(
																	(b) =>
																		b[
																			"London Borough"
																		] ===
																		geo
																			.properties
																			.LAD13NM
																)[
																	heatmapSource +
																		"_colour"
																]
															}
															style={{
																default: {
																	outline:
																		"none",
																},
																hover: {
																	cursor: "pointer",
																	outline:
																		"none",
																},
																pressed: {
																	outline:
																		"none",
																},
															}}
															onClick={() => {
																setSelectedBorough(
																	geo
																		.properties
																		.LAD13NM
																);
																setSearchFieldValue(
																	geo
																		.properties
																		.LAD13NM
																);
																setSearchArea(
																	"Boroughs"
																);
															}}
														/>
													</CustomTooltip>

													<Marker
														coordinates={currentBorough[
															"Center Coordinates"
														]
															.split(",")
															.map((coord) =>
																parseFloat(
																	coord
																)
															)}
														onClick={() => {
															setSelectedBorough(
																geo.properties
																	.LAD13NM
															);
															setSearchFieldValue(
																geo.properties
																	.LAD13NM
															);
															setSearchArea(
																"Boroughs"
															);
														}}
													>
														<text
															textAnchor="middle"
															className="borough-acronym-text"
														>
															{
																currentBorough[
																	"Borough acronym (According to Atlas)"
																]
															}
														</text>
													</Marker>
												</>
											);
										})
									}
								</Geographies>
							</ComposableMap>
						</div>
						<div className="map-legend-container">
							<div className="map-legend">
								<div className="legend-item">
									<div
										className="legend-swatch"
										style={{
											backgroundColor: colorPalette[0],
										}}
									></div>
									<div className="legend-label">
										0-
										{heatmapIntervals[heatmapSource][0]}
									</div>
								</div>
								{heatmapIntervals[heatmapSource].map(
									(interval, index) => (
										<div
											className="legend-item"
											key={index}
										>
											<div
												className="legend-swatch"
												style={{
													backgroundColor:
														colorPalette[index + 1],
												}}
											></div>
											<div className="legend-label">
												{index ===
												heatmapIntervals[heatmapSource]
													.length -
													1
													? `${interval + 1}+`
													: `${interval + 1}-${
															heatmapIntervals[
																heatmapSource
															][index + 1]
													  }`}
											</div>
										</div>
									)
								)}
							</div>
							<Typography
								variant="body2"
								style={{ marginRight: 16 }}
							>
								Number of people rough sleeping
							</Typography>
						</div>
					</div>

					<div id="data-dashboard-service-centers-container">
						<div id="service-information-header">
							<Typography
								variant="h5"
								sx={{ fontWeight: "bold" }}
							>
								Funding Information
							</Typography>
						</div>
						{selectedBorough === null ? (
							<div id="data-dashboard-notification-text">
								<Typography variant="body1">
									No data available.
									<br />
									Select a borough to view the available
									funding data.
								</Typography>
							</div>
						) : (
							<>
								<Typography
									className="borough-preview-text"
									variant="h5"
								>
									{selectedBorough.toUpperCase()}{" "}
								</Typography>

								<div className="funding-tab">
									<div>RSDATG</div>
									<CustomTooltip
										title={
											<div>
												The{" "}
												<a
													className="homeless_link"
													href="https://www.gov.uk/government/news/extra-help-for-rough-sleepers-with-drug-and-alcohol-dependency"
													target="_blank"
													rel="noreferrer"
												>
													Drug and Alcohol Treatment
													Grant
												</a>
												, later known as the Rough Sleeping Drug and Alcohol Treatment Grant (RSDATG), was initiated in 2020. In its first year, it supported people sleeping rough in 43 councils (including 19 London Local Authorities) to access structured drug and alcohol treatment including counselling, detox and rehabilitation services (in partnership with the Department of Health and Social Care and managed by Public Health England). In the next year (2021/2022), 20 more areas were included, and the supported London Boroughs were 21. In 2022, the Government announced up to £186.5 million for the next 3 years of the{" "}
												<a
													className="homeless_link"
													href="https://www.gov.uk/government/publications/rough-sleeping-drug-and-alcohol-treatment-grant-2022-to-2024-funding-allocations"
													target="_blank"
													rel="noreferrer"
												>
													RSDATG
												</a>
												. This commitment builds on the last years of funding for this programme, which funds local areas to implement evidence-based drug and alcohol treatment and wrap-around support for people sleeping rough or at risk of sleeping rough, including those with co-occurring mental health needs. This latest round of funding has expanded the grant to an additional 20 areas, bringing the total to 83 areas (including 24 London Local Authorities) and 5 pan-London projects.
											</div>
										}
										placement="bottom-end"
										enterTouchDelay={0}
									>
										<InfoRounded
											style={{
												color: "#A569B6",
												cursor: "pointer",
												height: "15px",
												marginLeft: "5px",
											}}
										/>
									</CustomTooltip>
								</div>

								<TableContainer className="fundings-table">
									{typeof rsdatgAppear !== "undefined" && (
										<Table
											size="small"
											className="fundings-table"
										>
											<TableHead className="custom-table-header">
												<TableRow>
													<TableCell>
														2020-21
													</TableCell>

													<TableCell>
														2021-22
													</TableCell>

													<TableCell>
														2022-23
													</TableCell>

													<TableCell>
														2023-24
													</TableCell>
												</TableRow>
											</TableHead>

											<TableBody>
												<TableRow>
													<TableCell
														component="th"
														scope="row"
													>
														{rsdatgAppear[
															"Funding2020/21"
														]
															? rsdatgAppear[
																	"Funding2020/21"
															  ].replace(
																	".0",
																	""
															  )
															: "N/A"}
													</TableCell>

													<TableCell>
														{rsdatgAppear[
															"Funding2021/22"
														]
															? rsdatgAppear[
																	"Funding2021/22"
															  ].replace(
																	".0",
																	""
															  )
															: "N/A"}
													</TableCell>

													<TableCell>
														{rsdatgAppear[
															"Funding2022/23"
														]
															? rsdatgAppear[
																	"Funding2022/23"
															  ].replace(
																	".0",
																	""
															  )
															: "N/A"}
													</TableCell>

													<TableCell>
														{rsdatgAppear[
															"Funding2023/24"
														]
															? rsdatgAppear[
																	"Funding2023/24"
															  ].replace(
																	".0",
																	""
															  )
															: "N/A"}
													</TableCell>
												</TableRow>
											</TableBody>
										</Table>
									)}
								</TableContainer>
								<div className="funding-tab">
									<div>RSI</div>
									<CustomTooltip
										title={
											<div>
												The{" "}
												<a
													className="homeless_link"
													href="https://www.gov.uk/government/news/councils-given-further-200-million-in-next-stage-of-successful-rough-sleeping-programme"
													target="_blank"
													rel="noreferrer"
												>
													Rough Sleeping Initiative
												</a>{" "}
												(RSI) was first announced in March 2018 to make an immediate impact on the rising levels of rough sleeping. RSI aims to help local areas provide tailored services to those living on the streets. With this investment, the government has been allocating funds to support coordinated projects across areas including housing, mental health, addiction support, and domestic abuse. The allocations have been used by Local Authorities, charities, and other organisations. For the {" "}
												<a
													className="homeless_link"
													href="https://www.gov.uk/government/publications/rough-sleeping-initiative-2022-to-2025-funding-allocations"
													target="_blank"
													rel="noreferrer"
												>
													2022-2025 period
												</a>{" "}												
												, the government has announced funding allocations of up to £500m in 303 areas, including all London Local Authorities.
											</div>
										}
										placement="bottom-end"
										enterTouchDelay={0}
									>
										<InfoRounded
											style={{
												color: "#A569B6",
												cursor: "pointer",
												height: "15px",
												marginLeft: "5px",
											}}
										/>
									</CustomTooltip>
								</div>

								<TableContainer className="fundings-table">
									{typeof rsiAppear !== "undefined" && (
										<Table
											size="small"
											className="fundings-table"
										>
											<TableHead className="custom-table-header">
												<TableRow>
													<TableCell>
														2020-21
													</TableCell>

													<TableCell>
														2022-25
													</TableCell>
												</TableRow>
											</TableHead>

											<TableBody>
												<TableRow>
													<TableCell
														component="th"
														scope="row"
													>
														{rsiAppear["2021-22"]
															? rsiAppear[
																	"2021-22"
															  ].replace(
																	".0",
																	""
															  )
															: "N/A"}
													</TableCell>

													<TableCell>
														{rsiAppear["2022-25"]
															? rsiAppear[
																	"2022-25"
															  ].replace(
																	".0",
																	""
															  )
															: "N/A"}
													</TableCell>
												</TableRow>
											</TableBody>
										</Table>
									)}
									<div id="data-dashboard-help-text">
										<Typography variant="body2">
											The years displayed reflect the
											different funding cycles of the two
											funds.
										</Typography>
										{(selectedBorough ===
											"City of London" ||
											selectedBorough === "Hackney") && (
											<Typography
												variant="body2"
												style={{ marginTop: 10 }}
											>
												The figures on funding from
												RSDATG and RSI for Hackney and
												the City of London are presented
												together.
											</Typography>
										)}
									</div>
								</TableContainer>
							</>
						)}
					</div>
				</div>
			) : (
				<div style={{ height: "calc(100vh - 375px" }} />
			)}
		</div>
	);
}
export default Home;
